import SetupStopInput from './SetupStopInput'
import { useState, useEffect, useMemo, useCallback, useRef, useImperativeHandle } from 'react'
import { X, Check, AlertTriangle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useOrderGateway } from '../../hooks/useOrderGateway'
import { useBiometricGate } from '../../hooks/useBiometricGate'
import { useAccountNicknames } from '../../hooks/useAccountNicknames'
import AccountName from '../AccountName'
import PrivateValue from '../PrivateValue'
import OrderConfirmationModal from '../OrderConfirmationModal'
import { splitGroupHasPartialFailure } from '../../lib/bracketOrderGroups.js'
import { buildSetupOrderRequests } from '../../lib/setupOrderRequests.js'
import { hasLiveSetupOrderEvidence } from '../../lib/setupRepairPlan.js'

const fmtCurrency = (val) => {
  if (val == null) return '\u2014'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const DEFAULT_RISK_PCT = 0.075

export default function SetupStagingPanel({
  project,
  setupData,
  symbol,
  referencePrice = null,
  isAddTranche = false,
  isManual = false,
  hideClose = false,
  onCancel,
  onSend,
  onSave,
  onClearSetup,
  editorRef = null,
  onCloseReview = null,
  onPreviewUpdate,
  onPositionRefresh,
  onRepairMissingOrders = null,
  // EF-159b: ephemeral-mode integration. When project?.id is null and
  // isEphemeral is true, Save Draft / Send Orders insert the row by calling
  // insertEphemeralProject (returns the newly-created project), then defer
  // URL transition via onEphemeralFinalized until the commit (and order
  // placement, for Send Orders) has settled. Without these props the panel
  // works as before — the prior project?.id guards still apply for safety.
  isEphemeral = false,
  insertEphemeralProject = null,
  onEphemeralFinalized = null,
}) {
  const { user } = useAuth()
  const { getNickname } = useAccountNicknames()
  const biometricGate = useBiometricGate()

  // Editable fields
  const [pattern, setPattern] = useState(setupData?.pattern || '')
  const [note, setNote] = useState(setupData?.note ?? project?.setup_notes ?? '')
  const [entryType, setEntryType] = useState(setupData?.entryType || 'Pullback')
  const [triggerPrice, setTriggerPrice] = useState(setupData?.triggerPrice || '')
  const [stopPrice, setStopPrice] = useState(setupData?.stopPrice || '')
  // A saved null target is intentional; a fresh manual setup still defaults to 2R.
  const [targetEnabled, setTargetEnabled] = useState(() => isManual
    || !Object.prototype.hasOwnProperty.call(project || {}, 'sell_target_price')
    || Number(setupData?.targetPrice) > 0)
  const [targetPrice, setTargetPrice] = useState(setupData?.targetPrice || '')
  const [sizePct, setSizePct] = useState(setupData?.sizePct || 9)
  const [invalidOrderIntent, setInvalidOrderIntent] = useState(null)

  // EF-119: track whether the user has manually edited the target field
  // in this session. If they have, stop auto-filling — they know what
  // they want. EF-118's previous "skip if target > 0" guard meant a
  // setup loading from the DB with a stale target stayed stale.
  const targetTouched = useRef(false)

  // Auto-fill target at 2R whenever trigger or stop changes, unless the
  // user has manually typed a target in this session. Always overwrites
  // a stale value that came from setupData.targetPrice — the user can
  // override afterwards by typing into the field.
  useEffect(() => {
    if (!targetEnabled || targetTouched.current) return
    const trigger = Number(triggerPrice)
    const stop = Number(stopPrice)
    if (trigger > 0 && stop > 0) {
      const risk = Math.abs(trigger - stop)
      if (risk > 0) setTargetPrice((trigger + risk * 2).toFixed(2))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerPrice, stopPrice, targetEnabled])

  // EF-124: current market price (averaged across this user's open
  // schwab_positions for the symbol). Used to auto-pick entry type —
  // a trigger above market can only be a buy-stop (On Strength); a
  // trigger at-or-below is a limit (Pullback). Wrong choice gets the
  // order rejected by Schwab.
  const [currentPrice, setCurrentPrice] = useState(null)

  useEffect(() => {
    if (!user || !symbol) return
    let cancelled = false
    supabase
      .from('schwab_positions')
      .select('market_value, quantity')
      .eq('user_id', user.id)
      .eq('symbol', symbol)
      .then(({ data }) => {
        if (cancelled || !data?.length) return
        const totalMktVal = data.reduce((s, p) => s + (Number(p.market_value) || 0), 0)
        const totalQty = data.reduce((s, p) => s + (Number(p.quantity) || 0), 0)
        if (totalQty > 0) setCurrentPrice(totalMktVal / totalQty)
      })
    return () => { cancelled = true }
  }, [user, symbol])

  const activeReferencePrice = useMemo(() => {
    const lifted = Number(referencePrice)
    if (Number.isFinite(lifted) && lifted > 0) return lifted
    return currentPrice
  }, [referencePrice, currentPrice])

  const riskRewardLabel = useMemo(() => {
    const trigger = Number(triggerPrice)
    const stop = Number(stopPrice)
    const target = Number(targetPrice)
    const risk = Math.abs(trigger - stop)
    const reward = Math.abs(target - trigger)

    if (!Number.isFinite(trigger) || !Number.isFinite(stop) || !Number.isFinite(target)) return '\u2014'
    if (trigger <= 0 || stop <= 0 || target <= 0 || risk <= 0 || reward <= 0) return '\u2014'

    return `1:${(reward / risk).toFixed(1)}`
  }, [triggerPrice, stopPrice, targetPrice])

  // EF-124: auto-select entry type based on trigger vs current price.
  // The user can still manually click the toggle to override; their
  // override sticks until the trigger price changes again.
  useEffect(() => {
    if (activeReferencePrice == null) return
    const trigger = Number(triggerPrice)
    if (!trigger || trigger <= 0) return
    setEntryType(trigger > activeReferencePrice ? 'On Strength' : 'Pullback')
  }, [triggerPrice, activeReferencePrice])

  // Account data
  const [accounts, setAccounts] = useState([])
  const [selectedAccounts, setSelectedAccounts] = useState(new Set())
  const [accountsLoaded, setAccountsLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [clearError, setClearError] = useState(null)
  const [saveError, setSaveError] = useState(null)
  // Opening a card is not an edit. Once the operator changes a control,
  // summarize every field that saving would change from the loaded setup,
  // including any calculated target/entry adjustment.
  const loadedFields = useRef({
    pattern, note, entryType,
    triggerPrice: Number(triggerPrice) || 0,
    stopPrice: Number(stopPrice) || 0,
    targetPrice: Number(targetPrice) || 0,
    sizePct: Number(sizePct) || 0,
  })
  const editBaseline = useRef(null)
  const draftSnapshot = () => ({
    pattern, note, entryType,
    triggerPrice: Number(triggerPrice) || 0,
    stopPrice: Number(stopPrice) || 0,
    targetPrice: Number(targetPrice) || 0,
    sizePct: Number(sizePct) || 0,
    accounts: [...selectedAccounts].sort(),
  })
  const markEdited = () => {
    if (!editBaseline.current) editBaseline.current = draftSnapshot()
  }
  const summarizeEdits = () => {
    if (!editBaseline.current) return []
    const after = draftSnapshot()
    if (JSON.stringify(editBaseline.current) === JSON.stringify(after)) return []
    const before = { ...editBaseline.current, ...loadedFields.current }
    const fields = [
      ['pattern', 'Name', value => value || 'Untitled setup'],
      ['note', 'Note', value => value || 'None'],
      ['entryType', 'Entry', value => value],
      ['triggerPrice', 'Trigger', fmtCurrency],
      ['stopPrice', 'Stop', fmtCurrency],
      ['targetPrice', 'Target', value => value > 0 ? fmtCurrency(value) : 'None'],
      ['sizePct', 'Size', value => `${value}%`],
      ['accounts', 'Accounts', values => values.map(value => getNickname(value) || value).join(', ') || 'None'],
    ]
    return fields.filter(([key]) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
      .map(([key, label, format]) => `${label}: ${format(before[key])} → ${format(after[key])}`)
  }

  // Load account equity
  useEffect(() => {
    if (!user) return
    setAccountsLoaded(false)
    ;(async () => {
      const { data } = await supabase
        .from('schwab_accounts')
        .select('account_number, nick_name, account_type, equity')
        .eq('user_id', user.id)
      const accts = (data || []).map(a => {
        const eq = a.equity
        const equityVal = typeof eq === 'object' ? (eq.liquidationValue || eq.totalAccountValue || 0) : Number(eq) || 0
        return { account_number: a.account_number, nick_name: a.nick_name || a.account_type, equity: equityVal }
      }).filter(a => a.equity > 0)
      setAccounts(accts)

      // IS-01b: respect imported account selection.
      // project.account_sizing is populated by the AGI import and refreshed
      // by this panel on save/send. When it has entries, only those accounts
      // are pre-selected. Manual drafts and setups without account_sizing
      // fall through to "select all with equity > 0".
      const sizingNumbers = (project?.account_sizing || []).map(s => s.account_number)
      if (sizingNumbers.length > 0) {
        const filtered = accts.filter(a => sizingNumbers.includes(a.account_number))
        setSelectedAccounts(new Set(filtered.map(a => a.account_number)))
      } else {
        setSelectedAccounts(new Set(accts.map(a => a.account_number)))
      }
      setAccountsLoaded(true)
    })()
  }, [user?.id, project?.id])

  // Calculate sizing when prices change
  const accountSizing = useMemo(() => {
    const trigger = Number(triggerPrice) || 0
    const stop = Number(stopPrice) || 0
    const alloc = Number(sizePct) / 100
    if (!(trigger > 0) || !(stop > 0) || !Number.isFinite(stop)) return accounts.map(a => ({ ...a, shares: 0, totalCost: 0, dollarRisk: 0 }))
    const stopDist = Math.abs(trigger - stop)

    return accounts.map(a => {
      const perTranche = a.equity * alloc
      const allocShares = Math.floor(perTranche / trigger)
      const maxLoss = perTranche * DEFAULT_RISK_PCT
      const riskShares = stopDist > 0 ? Math.floor(maxLoss / stopDist) : allocShares
      const shares = Math.min(allocShares, riskShares)
      return { ...a, shares, totalCost: shares * trigger, dollarRisk: shares * stopDist }
    })
  }, [accounts, triggerPrice, stopPrice, sizePct])

  // Notify the workspace of the exact setup-card preview. Price lines and
  // current-equity sizing travel together so the header cannot mix saved
  // import sizing with the quantities this panel would review/send.
  useEffect(() => {
    if (onPreviewUpdate) {
      const t = Number(triggerPrice) || null
      const s = Number(stopPrice) || null
      const tgt = Number(targetPrice) || null
      if (t || s || tgt) {
        const previewSizing = accountsLoaded
          ? accountSizing
            .filter(account => selectedAccounts.has(account.account_number))
            .map(account => ({
              account_number: account.account_number,
              shares: account.shares,
              max_loss: account.dollarRisk,
            }))
          : null
        onPreviewUpdate({
          triggerPrice: t,
          patternFailurePrice: s,
          sellTargetPrice: tgt,
          accountSizing: previewSizing,
        })
      } else {
        onPreviewUpdate(null)
      }
    }
  }, [triggerPrice, stopPrice, targetPrice, accountsLoaded, accountSizing, selectedAccounts]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAccount = (acctNum) => {
    markEdited()
    setSelectedAccounts(prev => {
      const next = new Set(prev)
      if (next.has(acctNum)) next.delete(acctNum)
      else next.add(acctNum)
      return next
    })
  }

  // EF-159b: when Send Orders triggers an ephemeral INSERT, stash the
  // freshly-created project in a ref so the deferred URL transition has
  // access to it after the confirmation modal closes. We hold the
  // transition until AFTER onOrdersChanged so the position_projects
  // update inside it runs against the real id (project prop has
  // already re-flowed from PositionWorkspace.setProjects([created])).
  // On modal-cancel, we still finalize so the user lands on the real
  // workspace; the row stays in 'idea' stage because no order ever fired.
  const pendingEphemeralFinalizationRef = useRef(null)
  const pendingPartialSetupRefreshRef = useRef(false)

  // Order gateway
  const { omsEnabled, schwabAuthExpired, confirmState, showConfirmation, confirmOrder, cancelConfirmation } = useOrderGateway({
    biometricGate,
    onOrdersChanged: async (results) => {
      // A partial initial setup remains in this results modal so the trader
      // can repair it immediately. This is derived from the results already
      // in memory: no additional Schwab request is added to normal sends.
      const repairablePartial = !isAddTranche
        && typeof onRepairMissingOrders === 'function'
        && Array.isArray(results)
        && hasLiveSetupOrderEvidence(results)
        && results.some(row => row?.success !== true)
      pendingPartialSetupRefreshRef.current = repairablePartial
      const updateProject = project?.id ? project : pendingEphemeralFinalizationRef.current
      if (updateProject?.id) {
        // EF-120: persist the full setup data alongside the status
        // transition (sending orders bypassed handleSaveDraft, so the
        // chart's overlay lines disappeared after onSend cleared the
        // panel). EF-124: also check whether Schwab actually accepted
        // the orders — if every order in the batch was rejected, mark
        // the setup as 'failed' and DON'T transition the stage. The
        // gold "WORKING" pill on a setup with no live orders was
        // misleading.
        const allFailed = Array.isArray(results) && results.length > 0 && results.every(r => !r.success)
        // EF-189 v3: a half-placed split bracket (one tranche success, the
        // sibling failed) counts as a placement failure — the unattended
        // trading guarantee breaks if we mark such a setup 'working' while
        // one sibling went live and the other didn't. The gateway has
        // already issued a best-effort CANCEL on the orphan; here we just
        // gate the status transition. Per-result `manualCleanupRequired`
        // (set when rollback itself failed) is persisted further below
        // under role ORPHAN_NEEDS_CLEANUP so Brandon can find and hand-cancel.
        const hasPartialSplitFailure = splitGroupHasPartialFailure(results)
        const placementFailed = allFailed || hasPartialSplitFailure
        // Setup status represents live broker exposure, not whether every
        // requested order landed cleanly. A partial split rollback can fail
        // the requested batch while independent accounts (or a cleanup
        // orphan) remain live at Schwab. Those projects must stay in Working
        // so the trader cannot mistake live orders for a dead setup.
        const hasLiveOrderEvidence = hasLiveSetupOrderEvidence(results)
        const setupIsWorking = !placementFailed || hasLiveOrderEvidence

        const sizing = accountSizing
          .filter(a => selectedAccounts.has(a.account_number))
          .map(a => ({
            account_number: a.account_number,
            shares: a.shares,
            max_loss: a.dollarRisk,
          }))

        const updateFields = {
          setup_pattern: pattern,
          setup_notes: note,
          entry_type: entryType,
          trigger_price: Number(triggerPrice) || null,
          pattern_failure_price: Number(stopPrice) || null,
          sell_target_price: Number(targetPrice) || null,
          recommended_size_pct: Number(sizePct) || null,
          account_sizing: sizing,
          setup_status: setupIsWorking ? 'working' : 'failed',
          updated_at: new Date().toISOString(),
        }

        const placedAt = new Date().toISOString()
        const roleMap = { TARGET: 'TARGET_ENTRY', RUNNER: 'RUNNER_ENTRY', ENTRY: 'ENTRY' }

        // EF-189 v3 (post-Codex amend hardening): the live-tracking list is
        // independent of placementFailed. Every success result with an
        // orderId that the gateway did NOT roll back is a live order at
        // Schwab and must be tracked so the cancel/reconcile flows can
        // reach it. Mixed-outcome batches — e.g. one split group partially
        // failed but another account succeeded cleanly — used to drop the
        // independent success on the floor because the failed-path branch
        // only emitted ORPHAN_NEEDS_CLEANUP rows.
        const successfulLiveTracked = (Array.isArray(results) ? results : [])
          .filter(r => r && r.success === true && r.orderId != null)
          // Rollback-attempted successes are orphan-class — handled below
          // via ORPHAN_NEEDS_CLEANUP (rollback failed) or intentionally
          // dropped (rollback succeeded — no live order at Schwab).
          .filter(r => r.rollbackAttempted !== true)
          .map(r => ({
            accountNumber: r.account_number ?? r.accountNumber ?? null,
            orderId: r.orderId,
            bracketGroupId: r.bracketGroupId ?? null,
            role: roleMap[r.bracketRole] || 'ENTRY',
            placedAt,
          }))

        if (placementFailed) {
          // Failed path: persist orphan orderIds only when the gateway's
          // rollback itself failed (manualCleanupRequired). Healthy
          // rollback leaves no live order at Schwab — those successes are
          // already excluded from successfulLiveTracked above.
          const cleanupOrphans = (Array.isArray(results) ? results : [])
            .filter(r => r && r.manualCleanupRequired === true && r.orderId != null)
            .map(r => ({
              accountNumber: r.account_number ?? r.accountNumber ?? null,
              orderId: r.orderId,
              bracketGroupId: r.bracketGroupId ?? null,
              role: 'ORPHAN_NEEDS_CLEANUP',
              placedAt,
            }))
          const tracked = [...successfulLiveTracked, ...cleanupOrphans]
          if (tracked.length > 0) {
            updateFields.schwab_order_ids = tracked
          }
        }

        // Any confirmed live order makes this a working project. A partial
        // batch remains visibly actionable in Working while the card derives
        // its separate NEEDS ATTENTION signal from account/order evidence.
        // Only a batch with zero live orders stays in Idea/Planned as failed.
        if (setupIsWorking) {
          const currentStage = (updateProject?.stage || project?.stage)?.toLowerCase()
          if (currentStage === 'idea' || currentStage === 'planned') {
            updateFields.stage = 'working'
          }
        }

        if (!placementFailed) {
          // EF-179: capture the parent TRIGGER orderId from each successful
          // OMS result so PositionWorkspace can later cancel exactly the
          // orders this setup placed — never adjacent active-position
          // stops/targets at the same symbol+account. Replace, don't
          // concatenate: stale IDs from prior staging attempts (canceled
          // via the PositionBoard X or via ToS) would never get cleaned
          // out and would pollute future cancel batches.
          //
          // EF-189: split brackets emit two Schwab orders (target + runner)
          // per account; each result carries the originating bracketRole +
          // bracketGroupId merged in by useOrderGateway. Persist the role as
          // TARGET_ENTRY / RUNNER_ENTRY / ENTRY so the working-orders UI can
          // recombine the pair into one logical row.
          updateFields.schwab_order_ids = successfulLiveTracked
        }

        await supabase.from('position_projects').update(updateFields).eq('id', updateProject.id)
      }
      if (onSend) onSend()
      if (onPositionRefresh && !repairablePartial) onPositionRefresh()
      // EF-159b: finalize the ephemeral → real URL transition AFTER the
      // position_projects update and the onSend/onPositionRefresh hooks
      // have settled. The new mount picks up location.state.preloadedProject
      // and skips the loading flash.
      if (!repairablePartial && pendingEphemeralFinalizationRef.current && onEphemeralFinalized) {
        onEphemeralFinalized(pendingEphemeralFinalizationRef.current)
        pendingEphemeralFinalizationRef.current = null
      }
    },
  })

  // EF-159b: wrap cancelConfirmation so dismissing the modal AFTER an
  // ephemeral insert still finalizes the URL transition. Without this,
  // the user would cancel, the row would exist in Supabase as a stranded
  // IDEA, and refreshing could re-create the synthetic project and force a
  // second insert attempt. Finalizing the selection keeps workspace state
  // aligned with the real row while the visible URL remains generic; the
  // setup_status remains 'idea' (no orders sent, no transition fired).
  const handleCancelConfirmation = useCallback(async () => {
    cancelConfirmation()
    if (pendingEphemeralFinalizationRef.current && onEphemeralFinalized) {
      onEphemeralFinalized(pendingEphemeralFinalizationRef.current)
      pendingEphemeralFinalizationRef.current = null
    }
    if (pendingPartialSetupRefreshRef.current) {
      pendingPartialSetupRefreshRef.current = false
      if (onPositionRefresh) await onPositionRefresh()
    }
  }, [cancelConfirmation, onEphemeralFinalized, onPositionRefresh])

  const handleRepairMissingOrders = useCallback(async () => {
    cancelConfirmation()
    if (pendingEphemeralFinalizationRef.current && onEphemeralFinalized) {
      onEphemeralFinalized(pendingEphemeralFinalizationRef.current)
      pendingEphemeralFinalizationRef.current = null
    }
    pendingPartialSetupRefreshRef.current = false
    if (onRepairMissingOrders) await onRepairMissingOrders()
    if (onPositionRefresh) await onPositionRefresh()
  }, [cancelConfirmation, onEphemeralFinalized, onPositionRefresh, onRepairMissingOrders])

  const getInvalidOrderIntent = useCallback((resolvedEntryType, trigger) => {
    const referencePrice = Number(activeReferencePrice)
    if (resolvedEntryType !== 'Pullback') return null
    if (!Number.isFinite(referencePrice) || referencePrice <= 0) return null
    if (!Number.isFinite(trigger) || trigger <= referencePrice) return null
    return { trigger, referencePrice }
  }, [activeReferencePrice])

  const stageOrders = async (resolvedEntryType) => {
    const trigger = Number(triggerPrice)
    const stop = Number(stopPrice)
    const target = Number(targetPrice)
    if (!(trigger > 0) || !(stop > 0) || !Number.isFinite(stop)) return

    const activeSizing = accountSizing.filter(a => selectedAccounts.has(a.account_number) && a.shares > 0)
    if (activeSizing.length === 0) return

    // EF-159b: ephemeral mode → INSERT the row BEFORE showing the
    // confirmation modal. This way the onOrdersChanged closure that fires
    // after user confirms sees project?.id as the real id (the project
    // prop has re-flowed from setProjects([created]) in PositionWorkspace).
    // We MUST defer the URL navigate until after orders confirm or the
    // user cancels — navigating mid-handler would remount the panel and
    // abort the modal flow.
    if (!project?.id) {
      if (!insertEphemeralProject) return
      const sizing = activeSizing.map(a => ({
        account_number: a.account_number, shares: a.shares, max_loss: a.dollarRisk,
      }))
      try {
        const created = await insertEphemeralProject({
          setup_pattern: pattern,
          setup_notes: note,
          entry_type: resolvedEntryType,
          trigger_price: trigger,
          pattern_failure_price: stop,
          sell_target_price: target || null,
          recommended_size_pct: Number(sizePct) || null,
          account_sizing: sizing,
        })
        pendingEphemeralFinalizationRef.current = created
      } catch {
        return
      }
    }

    const { orderSummaries, gatewayOrders } = buildSetupOrderRequests({
      symbol,
      entryType: resolvedEntryType,
      triggerPrice: trigger,
      stopPrice: stop,
      targetPrice: target,
      accountSizing,
      selectedAccountNumbers: selectedAccounts,
      getNickname,
    })

    if (gatewayOrders.length === 0) return
    showConfirmation(isAddTranche ? 'ADD_TRANCHE' : 'STAGE_ORDERS', symbol, orderSummaries, gatewayOrders, null)
  }

  const handleSendOrders = async () => {
    const trigger = Number(triggerPrice)
    const stop = Number(stopPrice)
    if (!(trigger > 0) || !(stop > 0) || !Number.isFinite(stop)) return

    const activeSizing = accountSizing.filter(a => selectedAccounts.has(a.account_number) && a.shares > 0)
    if (activeSizing.length === 0) return

    const invalid = getInvalidOrderIntent(entryType, trigger)
    if (invalid) {
      setInvalidOrderIntent(invalid)
      return
    }

    await stageOrders(entryType)
  }

  const handleSwitchInvalidOrderToStrength = async () => {
    setEntryType('On Strength')
    setInvalidOrderIntent(null)
    await stageOrders('On Strength')
  }

  const handleSaveDraft = async () => {
    if (!user || saving) return { ok: false, error: 'Unable to save right now.' }
    if (String(stopPrice).trim() && (!Number.isFinite(Number(stopPrice)) || Number(stopPrice) < 0)) {
      const error = 'Enter a valid stop price. For a negative percentage, press Enter in Stop first.'
      setSaveError(error)
      return { ok: false, error }
    }
    setSaveError(null)
    setSaving(true)
    try {
      const sizing = accountSizing.filter(a => selectedAccounts.has(a.account_number)).map(a => ({
        account_number: a.account_number, shares: a.shares, max_loss: a.dollarRisk,
      }))
      const setupFields = {
        setup_pattern: pattern,
        setup_notes: note,
        entry_type: entryType,
        trigger_price: Number(triggerPrice) || null,
        pattern_failure_price: Number(stopPrice) || null,
        sell_target_price: Number(targetPrice) || null,
        recommended_size_pct: Number(sizePct) || null,
        account_sizing: sizing,
        setup_status: 'idea',
        updated_at: new Date().toISOString(),
      }

      // EF-159b: ephemeral mode → INSERT with the setup fields in one shot
      // instead of UPDATE. The ref-stored promise inside insertEphemeralProject
      // guards against double-click duplication.
      let createdFromEphemeral = null
      if (!project?.id) {
        if (!insertEphemeralProject) throw new Error('Unable to create this setup.')
        createdFromEphemeral = await insertEphemeralProject(setupFields)
        if (!createdFromEphemeral?.id) throw new Error('The setup was not saved. Please retry.')
      } else {
        const { error } = await supabase.from('position_projects').update(setupFields).eq('id', project.id)
        if (error) throw new Error(error.message || 'The setup was not saved. Please retry.')
      }

      if (onSave) onSave({ setupFields, project: createdFromEphemeral || project })
      if (onPositionRefresh) await onPositionRefresh()
      // Defer the URL transition until AFTER the INSERT has resolved, so the
      // next mount can use location.state.preloadedProject to skip the
      // loading flash.
      if (createdFromEphemeral && onEphemeralFinalized) {
        onEphemeralFinalized(createdFromEphemeral)
      }
      loadedFields.current = { pattern, note, entryType, triggerPrice: Number(triggerPrice) || 0, stopPrice: Number(stopPrice) || 0, targetPrice: Number(targetPrice) || 0, sizePct: Number(sizePct) || 0 }
      editBaseline.current = null
      return { ok: true }
    } catch (error) {
      const message = error?.message || 'The setup was not saved. Please retry.'
      setSaveError(message)
      return { ok: false, error: message }
    } finally {
      setSaving(false)
    }
  }

  const handleClearSetup = async () => {
    if (saving) return
    setSaving(true)
    setClearError(null)
    try {
      // A saved idea has a lifecycle. Retire it before clearing the editor;
      // failed or stale writes must leave the operator's fields intact.
      if (onClearSetup) {
        await onClearSetup()
        return
      }
      setPattern('')
      setNote('')
      setTriggerPrice('')
      setStopPrice('')
      setTargetPrice('')
      setSizePct(9)
      if (onPreviewUpdate) onPreviewUpdate(null)
      if (onCancel) onCancel()
    } catch (error) {
      setClearError(error?.message || 'Could not cancel setup. Refresh before retrying.')
    } finally {
      setSaving(false)
    }
  }

  const selectedSizing = accountSizing.filter(a => selectedAccounts.has(a.account_number))
  const totalShares = selectedSizing.reduce((s, a) => s + a.shares, 0)
  const totalValue = selectedSizing.reduce((s, a) => s + a.totalCost, 0)
  const totalRisk = selectedSizing.reduce((s, a) => s + a.dollarRisk, 0)
  const totalEquity = selectedSizing.reduce((s, a) => s + a.equity, 0)
  const positionSizePct = totalEquity > 0 ? ((totalValue / totalEquity) * 100).toFixed(2) : '0.00'
  const canSend = Number(triggerPrice) > 0 && Number(stopPrice) > 0 && totalShares > 0 && omsEnabled

  const requestClose = (afterClose = onCancel) => {
    if (saving) return
    const changes = summarizeEdits()
    if (changes.length && onCloseReview) {
      onCloseReview({
        projectId: project?.id,
        changes,
        discard: () => afterClose?.(),
        save: async () => {
          const result = await handleSaveDraft()
          if (result.ok) afterClose?.()
          return result
        },
      })
    } else {
      afterClose?.()
    }
  }
  useImperativeHandle(editorRef, () => ({ requestClose, isSaving: () => saving }))

  const inputCls = 'w-full bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[11px] text-ink placeholder:text-inkMuted focus:outline-none focus:border-ink/40'

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between px-1">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">
            {isAddTranche ? 'ADD-ON ORDER' : 'SEND ORDERS'}
          </div>
          {project?.setup_source && (
            <div className="font-mono text-[9px] text-inkMuted/60 mt-0.5">
              Source: {project.setup_source}
            </div>
          )}
        </div>
        {!hideClose && (
          <button aria-label="Close setup details" onClick={() => requestClose()} className="text-inkMuted hover:text-ink transition-colors p-0.5">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Editable setup fields */}
      <div className="bg-canvas border border-border rounded-xl p-3 space-y-2">
        <div>
          <input
            value={pattern}
            onChange={e => { markEdited(); setPattern(e.target.value) }}
            placeholder="Pattern name"
            className={inputCls}
          />
        </div>
        <label className="block font-mono text-[10px] text-inkMuted">Setup note
          <textarea aria-label="Setup note" rows={3} value={note} onChange={e => { markEdited(); setNote(e.target.value) }} placeholder="Thoughts, thesis, or reminders…" className={inputCls} />
        </label>
        <div className="flex gap-1">
          {['Pullback', 'On Strength'].map(t => (
            <button
              key={t}
              onClick={() => { markEdited(); setEntryType(t) }}
              className={`font-mono text-[9px] px-2 py-0.5 rounded-full font-semibold border transition-colors ${
                entryType === t
                  ? (t === 'On Strength' ? 'bg-forest/10 text-forest border-forest/30' : 'bg-violet-500/10 text-violet-400 border-violet-400/30')
                  : 'bg-canvas2 text-inkMuted border-border'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <div className="font-mono text-[8px] text-inkMuted uppercase tracking-wider mb-0.5">Trigger</div>
            <div className="relative">
              <span className="absolute left-2 top-1 font-mono text-[10px] text-inkMuted">$</span>
              <input type="number" step="0.01" value={triggerPrice} onChange={e => { markEdited(); setTriggerPrice(e.target.value) }} placeholder="0.00" className={`${inputCls} pl-4`} />
            </div>
          </div>
          <div>
            <div className="font-mono text-[8px] text-inkMuted uppercase tracking-wider mb-0.5">Stop</div>
            <div className="relative">
              <span className="absolute left-2 top-1 font-mono text-[10px] text-inkMuted">$</span>
              <SetupStopInput entryPrice={triggerPrice} value={stopPrice} onChange={value => { markEdited(); setStopPrice(value) }} className={`${inputCls} pl-4`} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between font-mono text-[8px] text-inkMuted uppercase tracking-wider mb-0.5">
              <span>Target</span>
              {targetEnabled && (
                <button type="button" aria-label="Remove target" title="Remove 1/3 target"
                  className="text-inkMuted hover:text-ink rounded focus-visible:outline focus-visible:outline-1"
                  onClick={() => { markEdited(); targetTouched.current = true; setTargetPrice(''); setTargetEnabled(false) }}>
                  <X size={12} />
                </button>
              )}
            </div>
            {targetEnabled ? (
              <div className="relative">
                <span className="absolute left-2 top-1 font-mono text-[10px] text-inkMuted">$</span>
                <input aria-label="Target price" type="number" step="0.01" value={targetPrice} onChange={e => { markEdited(); targetTouched.current = true; setTargetPrice(e.target.value) }} placeholder="0.00" className={`${inputCls} pl-4`} />
              </div>
            ) : (
              <button type="button" aria-label="Add target" className={`${inputCls} text-inkMuted hover:text-ink`}
                onClick={() => { markEdited(); targetTouched.current = false; setTargetEnabled(true) }}>
                + Add target
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="font-mono text-[8px] text-inkMuted uppercase tracking-wider">Size</div>
          <div className="relative w-16">
            <input type="number" step="0.5" value={sizePct} onChange={e => { markEdited(); setSizePct(e.target.value) }} className={`${inputCls} pr-4 text-right`} />
            <span className="absolute right-2 top-1 font-mono text-[10px] text-inkMuted">%</span>
          </div>
        </div>
      </div>

      {/* Account order table */}
      <div role="region" aria-label="Setup account orders" tabIndex={0}
        className="min-w-0 max-w-full overflow-x-auto bg-canvas border border-border rounded-xl focus-visible:outline focus-visible:outline-1">
        <table className="w-full whitespace-nowrap font-mono text-[10px]">
          <thead>
            <tr className="text-[9px] tracking-widest uppercase text-inkMuted border-b border-border">
              <th className="text-left py-1.5 px-3 w-6"></th>
              <th className="text-left py-1.5 px-1">Account</th>
              <th className="text-right py-1.5 px-1">Shares</th>
              <th className="text-right py-1.5 px-1">Entry</th>
              <th className="text-right py-1.5 px-1">Stop</th>
              <th className="text-right py-1.5 px-1">Risk</th>
              <th className="text-right py-1.5 px-3">Value</th>
            </tr>
          </thead>
          <tbody>
            {accountSizing.map(acct => {
              const isOn = selectedAccounts.has(acct.account_number)
              return (
                <tr
                  key={acct.account_number}
                  className={`border-t border-border/50 cursor-pointer hover:bg-canvas2/30 transition-colors ${!isOn ? 'opacity-40' : 'border-l-2 border-l-[#c4953a]'}`}
                  onClick={() => toggleAccount(acct.account_number)}
                >
                  <td className="py-1.5 px-3">
                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${
                      isOn ? 'bg-[#c4953a] border-[#c4953a]' : 'border-border bg-canvas'
                    }`}>
                      {isOn && <Check size={10} className="text-white" strokeWidth={3} />}
                    </div>
                  </td>
                  <td className="py-1.5 px-1 text-ink">
                    <AccountName accountNumber={acct.account_number} className="font-mono text-[10px]" />
                  </td>
                  <td className="py-1.5 px-1 text-right text-ink"><PrivateValue>{acct.shares}</PrivateValue></td>
                  <td className="py-1.5 px-1 text-right text-ink"><PrivateValue>{fmtCurrency(Number(triggerPrice) || 0)}</PrivateValue></td>
                  <td className="py-1.5 px-1 text-right text-ink"><PrivateValue>{fmtCurrency(Number(stopPrice) || 0)}</PrivateValue></td>
                  <td className="py-1.5 px-1 text-right text-ember"><PrivateValue>{fmtCurrency(acct.dollarRisk)}</PrivateValue></td>
                  <td className="py-1.5 px-3 text-right text-ink"><PrivateValue>{fmtCurrency(acct.totalCost)}</PrivateValue></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Risk summary */}
      <div className="bg-canvas border border-border rounded-xl p-3 space-y-1.5">
        <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted">Risk Summary</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <div className="font-mono text-[9px] text-inkMuted">Max Loss</div>
          <div className="font-mono text-[10px] text-right text-ember">
            <PrivateValue>{fmtCurrency(totalRisk)}</PrivateValue>
          </div>
          <div className="font-mono text-[9px] text-inkMuted">Position Size</div>
          <div className="font-mono text-[10px] text-right text-ink">
            <PrivateValue>{positionSizePct}% of portfolio</PrivateValue>
          </div>
          <div className="font-mono text-[9px] text-inkMuted">Risk/Reward</div>
          <div className="font-mono text-[10px] text-right text-ink">
            {riskRewardLabel}
          </div>
        </div>
      </div>

      {/* Summary + buttons */}
      <div className="font-mono text-[9px] text-inkMuted px-1">
        <PrivateValue>{selectedAccounts.size} accounts {'\u00b7'} {totalShares} shares {'\u00b7'} {fmtCurrency(totalValue)}</PrivateValue>
      </div>
      <div className="flex items-center gap-2">
        {(project?.id || (isEphemeral && insertEphemeralProject)) && (
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors disabled:opacity-40"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
        )}
        <button
          data-testid="clear-setup-draft-btn"
          onClick={handleClearSetup}
          disabled={saving}
          className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-ember/30 text-ember/80 hover:text-ember hover:bg-ember/10 transition-colors disabled:opacity-40"
        >
          {onClearSetup ? 'Cancel Setup' : 'Clear Draft'}
        </button>
        <button
          onClick={handleSendOrders}
          disabled={!canSend}
          className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-forest bg-forest/10 text-forest hover:bg-forest/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
        >
          Send Orders
        </button>
      </div>

      {saveError && <div role="alert" className="font-mono text-[10px] text-ember">{saveError}</div>}
      {clearError && <div role="alert" className="font-mono text-[10px] text-ember">{clearError}</div>}

      {schwabAuthExpired && (
        <div className="text-[9px] font-mono text-ember/80 text-center mt-1">
          Schwab auth expired — reconnect in Settings
        </div>
      )}

      {invalidOrderIntent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-canvas shadow-2xl">
            <div className="flex items-start gap-3 border-b border-border px-4 py-3">
              <div className="mt-0.5 rounded-full bg-ember/10 p-1.5 text-ember">
                <AlertTriangle size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[12px] font-bold uppercase tracking-widest text-ink">
                  Invalid Order Type
                </div>
                <div className="mt-1 text-[12px] leading-5 text-inkMuted">
                  Trigger {fmtCurrency(invalidOrderIntent.trigger)} is above reference price {fmtCurrency(invalidOrderIntent.referencePrice)}. Pullback would send a buy limit above market. Use On Strength to send a buy stop instead.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setInvalidOrderIntent(null)}
                className="rounded p-1 text-inkMuted transition-colors hover:bg-canvas2 hover:text-ink"
                aria-label="Close invalid order type"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3">
              <button
                type="button"
                onClick={() => setInvalidOrderIntent(null)}
                className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSwitchInvalidOrderToStrength}
                className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-forest bg-forest/10 text-forest hover:bg-forest/20 transition-colors"
              >
                Switch to On Strength
              </button>
            </div>
          </div>
        </div>
      )}

      <OrderConfirmationModal
        open={confirmState.open}
        onClose={handleCancelConfirmation}
        onConfirm={confirmOrder}
        loading={confirmState.loading}
        action={confirmState.action}
        symbol={confirmState.symbol}
        orders={confirmState.orders}
        results={confirmState.results}
        onRepairMissingOrders={typeof onRepairMissingOrders === 'function' ? handleRepairMissingOrders : null}
      />
    </div>
  )
}
